import { gql, useLazyQuery, useMutation } from '@apollo/client'
import AccessibilityNew from '@mui/icons-material/AccessibilityNew'
import Close from '@mui/icons-material/Close'
import FavoriteBorder from '@mui/icons-material/FavoriteBorder'
import HourglassEmpty from '@mui/icons-material/HourglassEmpty'
import AppBar from '@mui/material/AppBar'
import Box from '@mui/material/Box'
import Button from '@mui/material/Button'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import DialogTitle from '@mui/material/DialogTitle'
import Grid from '@mui/material/Grid'
import IconButton from '@mui/material/IconButton'
import Slide from '@mui/material/Slide'
import Toolbar from '@mui/material/Toolbar'
import Typography from '@mui/material/Typography'
import { Field, Form, Formik } from 'formik'
import { TextField } from 'formik-mui'
import I18n from 'i18n-js'
import { useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import Slider from 'react-slick'
import sanitizeHtml from 'sanitize-html'
import { makeStyles } from 'tss-react/mui'
import * as Yup from 'yup'

import BottomBeaconMover from '@src/lib/BottomBeaconMover'
import MenuItemWithCheckbox from '@src/lib/MenuItemWithCheckbox'
import normalizeLocaleName, {
  SUPPORTED_LANGUAGES,
} from '@src/lib/normalizeLocaleName'
import { userSelector } from '@src/stores/app/selectors'

import NotFound from '../../components/routes/NotFound'
import ChildSpacer from '../../lib/ChildSpacer'
import useIsInView from '../../lib/useIsInView'
import useIsMobileBreakpoint from '../../lib/useIsMobileBreakpoint'
import useModalState from '../../lib/useModalState'
import withThemeProvider from '../../lib/withThemeProvider'
import LegacySvg from '../shared/LegacySvg/LegacySvg'
import useTracking from '../shared/Tracking/useTracking'

import BusinessSelectDialog from './BusinessSelectDialog'
import LoginDialog from './LoginDialog'
import PrivacyPracticesBlurb from './PrivacyPracticesBlurb.tsx'

const query = gql`
  query getEnrollmentPageDetails($id: ID!, $linkId: ID!) {
    getBrand(
      id: $id
      params: {
        fields: [
          "default_enrollment_campaign"
          "industry_list"
          "campaign_descriptions"
        ]
      }
    ) {
      id
      locales
      name
      slug
      logo_url
      status
      industry_list
      default_enrollment_campaign {
        id
        description
        campaign_descriptions {
          id
          description
          language
        }
      }
    }
    getRegistrationKey(brandId: $id, linkId: $linkId) {
      id
      retailer_id
      state
    }
  }
`

const brandsRequiringKnownLocations = ['genesis', 'cbc']

function EnrollmentPage(props) {
  const isMobile = useIsMobileBreakpoint()
  const shouldDialogOpenImmediately =
    new URL(window.location).searchParams.get('showDialog') === 'true'
  const [dialogOpen, setDialogOpen] = useState(shouldDialogOpenImmediately)
  const currentUser = useSelector((state) => state.auth.user)
  const loggedIn = !!currentUser
  const hasNoRetailerAccounts =
    currentUser && currentUser.retailer_accounts.length === 0

  const closeDialog = () => setDialogOpen(false)

  const openDialog = () => setDialogOpen(true)

  const [runQuery, { data, loading, error }] = useLazyQuery(query)

  useEffect(() => {
    runQuery({
      variables: {
        id: props.match.params.brandId,
        linkId: props.match.params.registrationKey,
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.match.params.brandId, props.match.params.registrationKey])

  // Handle "user has no retailer accounts and pressed the login button on the
  // enrollment page".
  useEffect(() => {
    if (shouldDialogOpenImmediately && hasNoRetailerAccounts) {
      window.location = `/#/register/${props.match.params.brandId}/${props.match.params.registrationKey}/locations`
    }
  }, [
    hasNoRetailerAccounts,
    props.match.params.brandId,
    props.match.params.registrationKey,
    shouldDialogOpenImmediately,
  ])

  const directLinkToContentPage = useMemo(() => {
    if (!currentUser) {
      return
    }

    for (const retailerAccount of currentUser.retailer_accounts) {
      for (const retailer of retailerAccount.retailers) {
        if (retailer.brand.slug === props.match.params.brandId) {
          return `/retailer/content/${retailerAccount.slug}/all`
        }
      }
    }
  }, [currentUser, props.match.params.brandId])

  const canOnlyClaimExistingLocations = brandsRequiringKnownLocations.includes(
    props.match.params.brandId,
  )

  if (data || error) {
    const isBrandActive =
      data && data.getBrand && data.getBrand.status === 'active'
    const isRegistrationKeyEnabled =
      data &&
      data.getRegistrationKey &&
      data.getRegistrationKey.state === 'enabled'
    if (!isBrandActive || !isRegistrationKeyEnabled) {
      return (
        <div style={{ maxWidth: '960px', margin: '0 auto' }}>
          <NotFound />
        </div>
      )
    }
  }

  const nextEnrollmentStepPath =
    canOnlyClaimExistingLocations && directLinkToContentPage
      ? directLinkToContentPage
      : `/#/register/${props.match.params.brandId}/${props.match.params.registrationKey}/locations`

  if (
    canOnlyClaimExistingLocations &&
    loggedIn &&
    shouldDialogOpenImmediately
  ) {
    window.location = nextEnrollmentStepPath
    return null
  }

  if (loading || !data) {
    return null
  }

  const locale = I18n.locale
  const enrollmentCampaign = data.getBrand.default_enrollment_campaign
  const campaignDescription = enrollmentCampaign.campaign_descriptions.find(
    (description) => description.language === locale,
  )
  const brandMessage = campaignDescription
    ? campaignDescription.description
    : enrollmentCampaign.description

  return (
    <div>
      <>
        <PromoboxxBlurb
          brandName={data.getBrand.name}
          brandLocales={data.getBrand.locales}
          loggedIn={loggedIn}
          signupHref={
            hasNoRetailerAccounts ||
            (canOnlyClaimExistingLocations && currentUser)
              ? nextEnrollmentStepPath
              : undefined
          }
          openDialog={() => {
            if (
              hasNoRetailerAccounts ||
              (canOnlyClaimExistingLocations && currentUser)
            ) {
              return
            } else {
              openDialog()
            }
          }}
          industryList={data.getBrand.industry_list}
          showLanguageSelector={true}
        />
        {/* if user isn't logged in, open LoginDialog, else open BusinessSelectDialog */}
        {loggedIn ? (
          <BusinessSelectDialog
            brandId={data.getBrand.id}
            brandName={data.getBrand.name}
            retailerId={data.getRegistrationKey.retailer_id}
            closeDialog={closeDialog}
            open={dialogOpen}
          />
        ) : (
          <LoginDialog
            brandSlug={data.getBrand.slug}
            registrationKey={props.match.params.registrationKey}
            closeDialog={closeDialog}
            open={dialogOpen}
          />
        )}
      </>
      {isMobile ? (
        <>
          <BrandMessage
            brandLogo={data.getBrand.logo_url}
            brandMessage={brandMessage}
            brandName={data.getBrand.name}
          />
          <BulletPoints />
        </>
      ) : (
        <>
          <BulletPoints />
          <BrandMessage
            brandLogo={data.getBrand.logo_url}
            brandMessage={brandMessage}
            brandName={data.getBrand.name}
          />
        </>
      )}
      <VideoMessage
        brandName={data.getBrand.name}
        brandSlug={data.getBrand.slug}
        industryList={data.getBrand.industry_list}
      />
      <Footer brandName={data.getBrand.name} />
    </div>
  )
}

export default EnrollmentPage

const useLanguageChangerStyles = makeStyles()((theme) => ({
  positioning: {
    [theme.breakpoints.down('md')]: {
      marginBottom: theme.spacing(2),
      marginTop: theme.spacing(1),
    },
  },
  selectBox: {
    marginLeft: theme.spacing(2),
  },
}))

const UPDATE_USER = gql`
  mutation updateUserLanguage($user: UpdateUserInput!) {
    updateUser(user: $user) {
      user {
        id
      }
    }
  }
`

function LanguageChanger({ locales }) {
  const [isUpdating, setIsUpdating] = useState(false)
  const { classes: languageChangerClasses } = useLanguageChangerStyles()
  const [updateUser] = useMutation(UPDATE_USER)
  const user = useSelector(userSelector)
  const supportedLocales =
    locales ? locales : SUPPORTED_LANGUAGES
  if (!supportedLocales.includes('en-us')) {
    // en-us is always supported, even though a brand could not have it listed
    supportedLocales.push('en-us')
  }
  return (
    <div className={languageChangerClasses.positioning}>
      <Typography variant="overline" color="textSecondary">
        {I18n.t('languageSelector.language')}:
      </Typography>
      <select
        data-testid={'languageChanger'}
        className={languageChangerClasses.selectBox}
        disabled={isUpdating}
        onChange={async (event) => {
          setIsUpdating(true)

          // user is an empty object if logged out, so just checking for 'user' is not enough
          user.id
            ? await updateUser({
                variables: {
                  user: {
                    id: user.id,
                    language_preference: event.target.value,
                  },
                },
              })
            : window.localStorage.setItem('language', event.target.value)
          window.location.reload()
        }}
        value={normalizeLocaleName(
          (user && user.language_preference) ||
            window.localStorage.getItem('language'),
        )}
      >
        {supportedLocales.map((language) => (
          <option
            value={language}
            key={language}
            data-testid={`${language}Option`}
          >
            {I18n.t(`languageSelector.languages.${language}`)}
          </option>
        ))}
      </select>
    </div>
  )
}

// There's so much bespoke design here that using any numbers
// from the theme just doesn't make sense.
const useBlurbStyles = makeStyles()((theme) => ({
  positioning: {
    position: 'relative',
  },
  characterGuy: {
    position: 'absolute',
    transform: 'scale(1.78)',
    [theme.breakpoints.only('xs')]: {
      top: 7,
      right: '0%',
      transformOrigin: 'top right',
      marginBottom: 33,
    },
    [theme.breakpoints.up('sm')]: {
      top: 0,
      left: 0,
      transformOrigin: 'top left',
    },
  },
  logoContainer: {
    position: 'absolute',
    top: 0,
    [theme.breakpoints.only('xs')]: {
      left: 0,
      margin: 30,
      marginTop: 15,
    },
    [theme.breakpoints.up('sm')]: {
      right: 0,
      textAlign: 'right',
    },
  },
  loginBlurb: {
    display: 'block',
    marginTop: '-0.5em',
    [theme.breakpoints.only('xs')]: {
      marginTop: '-0.3em',
    },
  },
  loginNowButton: {
    marginLeft: '0.2em',
  },
  blurb: {
    fontWeight: 'normal',
    [theme.breakpoints.only('xs')]: {
      marginRight: 190,
    },
  },
  blurbContainer: {
    position: 'relative',
    zIndex: 1,
    [theme.breakpoints.up('sm')]: {
      marginTop: 100,
      marginBottom: 58,
      marginLeft: 183,
      marginRight: 50,
    },
    [theme.breakpoints.only('xs')]: {
      marginTop: 90,
      marginBottom: 25,
      marginLeft: 30,
      width: 225,
    },
  },
  buttonText: {
    whiteSpace: 'nowrap',
  },
  logo: {
    transform: 'scale(0.75)',
  },
  logoGrid: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.up('md')]: {
      justifyContent: 'flex-start',
    },
  },
  languageGrid: {
    width: '100%',
    display: 'flex',
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    [theme.breakpoints.up('md')]: {
      justifyContent: 'flex-end',
    },
  },
}))

function PromoboxxBlurb(props) {
  const { classes: blurbClass } = useBlurbStyles()
  const { classes: sharedClasses } = useSharedStyles()
  const [ref, isInView] = useIsInView({ initialValue: true })
  let currentUrl = decodeURIComponent(window.location.href)
  if (currentUrl.indexOf('?') === -1) {
    currentUrl = `${currentUrl}?showDialog=true`
  } else {
    currentUrl = `${currentUrl}&showDialog=true`
  }

  const { track } = useTracking()

  const trackBlurbSignUpNow = () => {
    track({
      event: 'Click',
      data: {
        type: 'enrollmentBlurbSignUpNow',
        brandName: props.brandName,
      },
    })
  }
  const trackAppBarSignUpNow = () => {
    track({
      event: 'Click',
      data: {
        type: 'enrollmentAppBarSignUpNow',
        brandName: props.brandName,
      },
    })
  }

  const trackLoginNow = () => {
    track({
      event: 'Click',
      data: {
        type: 'enrollmentLoginNowLaunch',
        brandName: props.brandName,
      },
    })
  }

  return (
    <>
      <Box>
        <div className={sharedClasses.limitedWidth}>
          <Grid container>
            <Grid item md={6} className={blurbClass.logoGrid}>
              <div className={blurbClass.logo}>
                <LegacySvg
                  height={`48px`}
                  symbol={'pbxx_logo_colored'}
                  width={`282px`}
                />
              </div>
            </Grid>
            {props.showLanguageSelector && (
              <Grid item md={6} className={blurbClass.languageGrid}>
                <LanguageChanger locales={props.brandLocales} />
              </Grid>
            )}
          </Grid>
          <div className={blurbClass.positioning}>
            {/* <span className={blurbClass.characterGuy}>
              <LegacySvg
                className={blurbClass.characterGuySvg}
                height={'205px'}
                symbol={svgFromIndustryList(props.industryList)}
                width={'89px'}
              />
            </span> */}
            <div className={blurbClass.logoContainer}>
              {props.loggedIn || (
                <Typography className={blurbClass.loginBlurb} variant="body1">
                  {I18n.t('auth.existingUserQuestionMark')}{' '}
                  <Button
                    className={blurbClass.loginNowButton}
                    color="secondary"
                    href={`/login?redirect_url=${window.encodeURIComponent(
                      currentUrl,
                    )}`}
                    onClick={trackLoginNow}
                    size="small"
                    variant="outlined"
                    data-testid={'loginNowButton'}
                  >
                    {I18n.t('enrollment.loginNowButtonText')}
                  </Button>
                </Typography>
              )}
            </div>
          </div>
          <div className={blurbClass.blurbContainer}>
            <Typography className={blurbClass.blurb} variant="h3">
              {I18n.t('enrollment.digitalMarketing')}{' '}
              <strong>{props.brandName}</strong>
            </Typography>
            <br />
            {/* //The callback in this onClick will hold the user object so it can
            //apply the logic within the openDialog() function and decide which dialog to open */}
            <Button
              color="secondary"
              href={props.signupHref}
              onClick={() => {
                trackBlurbSignUpNow()
                props.openDialog()
              }}
              ref={ref}
              size="large"
              variant="outlined"
              data-testid={'signUpNowBlurbButton'}
            >
              {I18n.t('enrollment.signUpNowButtonText')}
            </Button>
          </div>
        </div>
      </Box>
      <BottomBeaconMover />
      <Slide direction="up" in={!isInView}>
        <AppBar position="fixed" style={{ top: 'auto', bottom: 0 }}>
          <Toolbar className={sharedClasses.limitedWidth}>
            <ChildSpacer size={5}>
              <Button
                className={blurbClass.buttonText}
                color="secondary"
                href={props.signupHref}
                onClick={() => {
                  props.openDialog()
                  trackAppBarSignUpNow()
                }}
                size="large"
                variant="contained"
              >
                {I18n.t('enrollment.signUpNowButtonText')}
              </Button>
              <Typography variant="h6">
                {I18n.t('enrollment.getStartedWithBrand', {
                  brandName: props.brandName,
                })}
              </Typography>
            </ChildSpacer>
          </Toolbar>
        </AppBar>
      </Slide>
    </>
  )
}

// const industryCharacters = [
//   'Luxury Jewelry & Watches',
//   'Pet Care',
//   'Recreational Vehicles',
//   'Animal Nutrition',
//   'Appliances',
//   'Automotive',
//   'Cycling',
//   'Footwear Athletic',
//   'Health & Beauty',
//   'Outdoor Backpacks & Luggage',
//   'Outdoor General Sporting Goods',
//   'Outdoor Hard Goods',
//   'Outdoor Hunting & Fishing',
//   'Outdoor Power Equipment',
//   'Outdoor Soft Goods',
// ]

// const svgFromIndustryList = (industryList) => {
//   if (!industryList) {
//     return 'tall'
//   }
//   let randomIndustry =
//     industryList[Math.floor(Math.random() * industryList.length)]
//   let validIndustry = industryCharacters.includes(randomIndustry)
//     ? randomIndustry
//     : 'tall'

//   //some industries are not valid filenames so they need to be converted to valid strings
//   if (validIndustry.includes('Outdoor')) {
//     validIndustry = 'Outdoor'
//   }
//   if (validIndustry.includes('Health')) {
//     validIndustry = 'HB'
//   }
//   if (validIndustry.includes('Luxury')) {
//     validIndustry = 'Luxury'
//   }
//   //There are numerous Outdoor industries
//   return validIndustry.includes('Outdoor') ? 'Outdoor' : validIndustry
// }

const useSharedStyles = makeStyles()((theme) => ({
  limitedWidth: {
    [theme.breakpoints.up('sm')]: {
      width: 700,
      margin: 'auto',
    },
    [theme.breakpoints.up('md')]: {
      width: 800,
    },
    [theme.breakpoints.up('lg')]: {
      width: 1000,
    },
  },
  withPadding: {
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(12, 0),
    },
    padding: theme.spacing(12, 5),
  },
}))

const useBulletPointStyles = makeStyles()((theme) => ({
  // We need:
  // - Borders that fill the height of the container
  // - Everything centered vertically
  // - Some padding at the top and bottom of each list item
  // So with that, the outer element must have the border, as any padding could
  // get in the way of that.
  // Then, the inners must have a height of '100%', so we can then use flex to
  // align them in the center.
  bulletListItem: {
    '&:last-child': {
      borderRight: 'none',
    },
    [theme.breakpoints.up('sm')]: {
      borderRight: '1px solid rgba(0, 0, 0, 0.1)',
    },
  },
  bulletListItemInner: {
    textAlign: 'center',
    display: 'flex',
    justifyContent: 'top',
    flexDirection: 'column',
    height: '100%',
    [theme.breakpoints.only('xs')]: {
      width: 235,
      margin: 'auto',
      padding: theme.spacing(10, 0),
    },
    // This is really a sane default we should use everywhere, but we don't,
    // and setting it for everything might break things that were designed
    // with it in mind. Without this the padding makes the elements break
    // out of their container.
    boxSizing: 'border-box',
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(10, 5),
    },
  },
  iconSizing: {
    fontSize: '3.5em',
  },
  root: {
    [theme.breakpoints.up('lg')]: {
      marginTop: 55,
    },
  },
}))

function BulletPointsDesktop() {
  const { classes: bulletPointClasses } = useBulletPointStyles()
  const { classes: sharedClasses } = useSharedStyles()
  return (
    <Box bgcolor="background.default" className={bulletPointClasses.root}>
      <div className={sharedClasses.limitedWidth}>
        <Grid container>
          <Grid
            className={bulletPointClasses.bulletListItem}
            item
            sm={4}
            xs={12}
          >
            <div className={bulletPointClasses.bulletListItemInner}>
              <BulletListItem
                body={I18n.t('enrollment.bulletPoints.easy.body')}
                icon={
                  <AccessibilityNew
                    className={bulletPointClasses.iconSizing}
                    style={{ color: '#F37021' }}
                  />
                }
                title={I18n.t('enrollment.bulletPoints.easy.title')}
              />
            </div>
          </Grid>
          <Grid
            className={bulletPointClasses.bulletListItem}
            item
            sm={4}
            xs={12}
          >
            <div className={bulletPointClasses.bulletListItemInner}>
              <BulletListItem
                body={I18n.t('enrollment.bulletPoints.support.body')}
                icon={
                  <FavoriteBorder
                    className={bulletPointClasses.iconSizing}
                    style={{ color: '#904098' }}
                  />
                }
                title={I18n.t('enrollment.bulletPoints.support.title')}
              />
            </div>
          </Grid>
          <Grid
            className={bulletPointClasses.bulletListItem}
            item
            sm={4}
            xs={12}
          >
            <div className={bulletPointClasses.bulletListItemInner}>
              <BulletListItem
                body={I18n.t('enrollment.bulletPoints.timeSaving.body')}
                icon={
                  <HourglassEmpty
                    className={bulletPointClasses.iconSizing}
                    style={{ color: '#4BC6E4' }}
                  />
                }
                title={I18n.t('enrollment.bulletPoints.timeSaving.title')}
              />
            </div>
          </Grid>
        </Grid>
      </div>
    </Box>
  )
}

const useBulletPointsMobileStyles = makeStyles()({
  root: {
    paddingTop: 0,
    paddingLeft: 0,
    paddingRight: 0,
  },
  bulletListItem: {
    textAlign: 'center',
    boxSizing: 'border-box',
    paddingBottom: 0,
  },
  iconSizing: {
    fontSize: '3.5em',
  },
})

function BulletPointsMobile() {
  const { classes: bulletPointsMobileClasses } = useBulletPointsMobileStyles()
  const { classes: sharedClasses, cx } = useSharedStyles()
  return (
    <Box
      bgcolor="background.default"
      className={cx(sharedClasses.withPadding, bulletPointsMobileClasses.root)}
    >
      <Slider arrows={false} autoplay autoplaySpeed={5000} dots pauseOnHover>
        <div
          className={cx(
            bulletPointsMobileClasses.bulletListItem,
            sharedClasses.withPadding,
          )}
        >
          <BulletListItem
            body={I18n.t('enrollment.bulletPoints.easy.body')}
            icon={
              <AccessibilityNew
                className={bulletPointsMobileClasses.iconSizing}
                style={{ color: '#F37021' }}
              />
            }
            title={I18n.t('enrollment.bulletPoints.easy.title')}
          />
        </div>
        <div
          className={cx(
            bulletPointsMobileClasses.bulletListItem,
            sharedClasses.withPadding,
          )}
        >
          <BulletListItem
            body={I18n.t('enrollment.bulletPoints.support.body')}
            icon={
              <FavoriteBorder
                className={bulletPointsMobileClasses.iconSizing}
                style={{ color: '#904098' }}
              />
            }
            title={I18n.t('enrollment.bulletPoints.support.title')}
          />
        </div>
        <div
          className={cx(
            bulletPointsMobileClasses.bulletListItem,
            sharedClasses.withPadding,
          )}
        >
          <BulletListItem
            body={I18n.t('enrollment.bulletPoints.timeSaving.body')}
            icon={
              <HourglassEmpty
                className={bulletPointsMobileClasses.iconSizing}
                style={{ color: '#4BC6E4' }}
              />
            }
            title={I18n.t('enrollment.bulletPoints.timeSaving.title')}
          />
        </div>
      </Slider>
    </Box>
  )
}

function BulletPoints() {
  const isMobile = useIsMobileBreakpoint()
  return isMobile ? <BulletPointsMobile /> : <BulletPointsDesktop />
}

function BulletListItem(props) {
  return (
    <>
      <span>{props.icon}</span>
      <Typography variant="h6">{props.title}</Typography>
      <Typography color="textSecondary" variant="subtitle1">
        {props.body}
      </Typography>
    </>
  )
}

const useBrandMessageStyles = makeStyles()((theme) => ({
  brandImage: {
    maxHeight: 140,
    maxWidth: '100%',
    [theme.breakpoints.down('md')]: {
      maxHeight: 120,
    },
  },
  brandImageContainer: {
    display: 'flex',
    justifyContent: 'center',
    marginBottom: theme.spacing(8),
  },
  brandMessageContainer: {
    display: 'flex',
    alignItems: 'center',
    flexDirection: 'column',
  },
}))

function BrandMessage(props) {
  const [brandMessage, setBrandMessage] = useState(props.brandMessage)
  const { classes: sharedClasses } = useSharedStyles()
  const { classes: brandMessageClasses } = useBrandMessageStyles()

  // Brand can use this page to show a dynamic preview of the enrollment page.
  // This allows interaction and real-time updates from within Brand using an iframe
  useEffect(() => {
    window.top.postMessage({ type: 'brandMessage/mounted' }, '*')
    const updateMessage = ({ data }) => {
      const { type, html } = data
      if (type === 'updateHtml') {
        setBrandMessage(html)
      }
    }
    window.addEventListener('message', updateMessage)
    return () => window.removeEventListener('message', updateMessage)
  }, [])

  const sanitizedMessage = sanitizeHtml(brandMessage, {
    allowedTags: [
      'br',
      'p',
      'a',
      'li',
      'ul',
      'ol',
      'b',
      'i',
      'em',
      'strong',
      'u',
      'span',
    ],
    allowedAttributes: {
      a: ['href'],
    },
  }).replace(/<p><br[\s]*\/?><\/p>/g, '')

  return (
    <Box bgcolor="background.paper" className={sharedClasses.withPadding}>
      <div className={sharedClasses.limitedWidth}>
        <div className={brandMessageClasses.brandImageContainer}>
          <img
            alt="Brand Logo"
            className={brandMessageClasses.brandImage}
            src={props.brandLogo}
          />
        </div>
        <div className={brandMessageClasses.brandMessageContainer}>
          <Typography paragraph variant="h5">
            {I18n.t('enrollment.brandMessageHeader', {
              brandName: props.brandName,
            })}
          </Typography>
          <Typography variant="body1">
            {/* eslint-disable-next-line react/no-danger */}
            <span dangerouslySetInnerHTML={{ __html: sanitizedMessage }} />
          </Typography>
        </div>
      </div>
    </Box>
  )
}

const useVideoMessageStyles = makeStyles()((theme) => ({
  boxColor: {
    backgroundImage: 'linear-gradient(to right, #414B7A, #4B2F61)',
    color: theme.palette.text.primary,
  },
  reverseMobile: {
    flexDirection: 'column-reverse',
  },
  videoContainer: {
    height: 265,
  },
}))

const industryVideoIds = {
  'Health & Beauty': 'zwxcgp6z59',
  'Pet Care': 'oxotlp2e5r',
  'Animal Nutrition': 'oxotlp2e5r',
  'Cycling': '86tnz2rvkz',
  'Luxury Jewelry & Watches': 'hk4hrk6sct',
  'Recreational Vehicles': '91ffz0uwx2',
  'Flooring': '2v7e37ipr5',
  'Home Furnishings': '2v7e37ipr5',

  // This one is special because there's so many outdoor-adjacent verticals.
  '_Outdoor': 'c3rflte3b3',
}

const VideoMessage = withThemeProvider('dark')((props) => {
  const { track } = useTracking()

  const wistiaId =
    useMemo(() => {
      if (!props.industryList || props.industryList.length === 0) {
        return
      }

      let industry =
        props.industryList[
          Math.floor(Math.random() * props.industryList.length)
        ]

      if (industry.includes('Outdoor')) {
        industry = '_Outdoor'
      }

      return industryVideoIds[industry]
    }, [props.industryList]) || '9m7cgl4ers'

  useEffect(() => {
    const script = document.createElement('script')

    script.src = '//fast.wistia.com/assets/external/E-v1.js'
    script.setAttribute('async', true)
    document.head.appendChild(script)
    window._wq = window._wq || []

    window._wq.push({
      id: wistiaId,
      onReady: (video) => {
        video.bind('play', () => {
          track({
            event: 'Click',
            data: {
              type: 'enrollmentVideoPlay',
              brandName: props.brandName,
            },
          })
        })
      },
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const { classes: sharedClasses, cx } = useSharedStyles()
  const { classes: videoMessageClasses } = useVideoMessageStyles()

  return (
    <div
      className={cx(sharedClasses.withPadding, videoMessageClasses.boxColor)}
    >
      <div className={sharedClasses.limitedWidth}>
        <Grid alignItems="center" container spacing={6}>
          <Grid item sm={4} xs={12}>
            <Typography variant="body1">
              {I18n.t('enrollment.videoMessage')}
            </Typography>
          </Grid>
          <Grid item sm={8} xs={12}>
            <div
              className={cx(
                `wistia_embed wistia_async_${wistiaId}`,
                videoMessageClasses.videoContainer,
              )}
            />
          </Grid>
        </Grid>
        <PrivacyPracticesBlurb />
      </div>
    </div>
  )
})

const useFooterStyles = makeStyles()((theme) => ({
  toolbarSpacer: {
    ...theme.mixins.toolbar,
    whiteSpace: 'pre',
  },
  withPadding: {
    [theme.breakpoints.up('sm')]: {
      padding: theme.spacing(6, 0),
    },
    padding: theme.spacing(6, 5),
  },
}))

function Footer(props) {
  const { toggleModal, isOpen } = useModalState()

  const { classes: sharedClasses } = useSharedStyles()
  const { classes: footerClasses } = useFooterStyles()

  const { track } = useTracking()

  const handleNotInterestedClick = () => {
    toggleModal()
    track({
      event: 'Click',
      data: {
        type: 'enrollmentNotInterestedLaunched',
        brandName: props.brandName,
      },
    })
  }

  const trackContactUsClick = () => {
    track({
      event: 'Click',
      data: {
        type: 'enrollmentContactUsLaunched',
        brandName: props.brandName,
      },
    })
  }

  //Make two different types for each sign up now button

  return (
    <Box className={footerClasses.withPadding}>
      <div className={sharedClasses.limitedWidth}>
        <Grid container spacing={6}>
          <Grid item xs={6}>
            <Typography gutterBottom variant="body2">
              {I18n.t('enrollment.contactUsPreamble')}
            </Typography>
            <Button
              color="secondary"
              href="mailto:support@promoboxx.com"
              onClick={trackContactUsClick}
              size="small"
              variant="outlined"
              data-testid={'contactUsButton'}
            >
              {I18n.t('enrollment.contactUsButtonText')}
            </Button>
          </Grid>
          <Grid item style={{ textAlign: 'right' }} xs={6}>
            <Button
              onClick={handleNotInterestedClick}
              size="small"
              style={{ color: 'gray' }}
              data-testid={'notInterestedButton'}
            >
              {I18n.t('enrollment.notInterestedInThisBrandQuestionMark')}
            </Button>
          </Grid>
        </Grid>
        <div className={footerClasses.toolbarSpacer} />
      </div>
      <NotInterestedInBrand
        brandName={props.brandName}
        onClose={toggleModal}
        open={isOpen}
      />
    </Box>
  )
}

const mutation = gql`
  mutation submitNotInterested($data: CreateSupportEmailRequest!) {
    createSupportEmail(data: $data) {
      status
    }
  }
`

const useNotInterestedStyles = makeStyles()((theme) => ({
  closeButton: {
    position: 'absolute',
    right: theme.spacing(1),
    top: theme.spacing(1),
    color: theme.palette.grey[500],
  },
}))

const notInterestedInBrandSchema = Yup.object().shape({
  email_address: Yup.string()
    .email()
    .required(
      I18n.t('validations.required', { label: I18n.t('shared.emailAddress') }),
    ),
  message: Yup.string().required(
    I18n.t('validations.required', {
      label: I18n.t('enrollment.notInterestedDialog.selectReason'),
    }),
  ),
})

function NotInterestedInBrand(props) {
  const { classes: notInterestedClasses } = useNotInterestedStyles()

  const [submitNotInterested] = useMutation(mutation)

  const emailAddress = useSelector(
    (state) => state.auth && state.auth.user && state.auth.user.email,
  )
  const options = [
    I18n.t('enrollment.notInterestedDialog.reasonOne', {
      brandName: props.brandName,
    }),
    I18n.t('enrollment.notInterestedDialog.reasonTwo'),
    I18n.t('enrollment.notInterestedDialog.otherReason'),
  ]

  const { track } = useTracking()

  const initialValues = {
    email_address: emailAddress || '',
    message: options[0],
    other_reason: '',
  }

  return (
    <Formik
      initialValues={initialValues}
      isInitialValid={() =>
        notInterestedInBrandSchema.isValidSync(initialValues)
      }
      onSubmit={async (values, actions) => {
        const data = {
          email_address: values.email_address,
          message:
            values.message ===
            I18n.t('enrollment.notInterestedDialog.otherReason')
              ? values.other_reason
              : values.message,
        }
        try {
          track({
            event: 'Click',
            data: {
              type: 'enrollmentNotInterestedSubmit',
              brandName: props.brandName,
            },
          })

          await submitNotInterested({
            variables: {
              data,
            },
          })
          window.location = '/'
        } catch (error) {
          console.error(error)
        }
        actions.setSubmitting(false)
      }}
      validationSchema={notInterestedInBrandSchema}
    >
      {({ isSubmitting, isValid, values }) => {
        return (
          <Dialog
            fullWidth
            maxWidth="sm"
            onClose={props.onClose}
            open={props.open}
          >
            <IconButton
              aria-label="close"
              className={notInterestedClasses.closeButton}
              onClick={props.onClose}
              size="large"
            >
              <Close />
            </IconButton>
            <DialogTitle>
              {I18n.t('enrollment.notInterestedDialog.title', {
                brandName: props.brandName,
              })}
            </DialogTitle>
            <Form>
              <DialogContent>
                <Field
                  component={TextField}
                  fullWidth
                  label={I18n.t('shared.emailAddress')}
                  name={'email_address'}
                  type={'email'}
                  data-testid={'notInterestedEmailField'}
                />
                <Field
                  component={TextField}
                  fullWidth
                  label={I18n.t('enrollment.notInterestedDialog.selectReason')}
                  name={'message'}
                  select
                >
                  {options.map((option) => (
                    <MenuItemWithCheckbox key={option} value={option}>
                      {option}
                    </MenuItemWithCheckbox>
                  ))}
                </Field>
                {values.message ===
                  I18n.t('enrollment.notInterestedDialog.otherReason') && (
                  <Field
                    component={TextField}
                    fullWidth
                    label={I18n.t('enrollment.notInterestedDialog.letUsKnow')}
                    name={'other_reason'}
                  />
                )}
              </DialogContent>
              <DialogActions>
                <Button
                  color="primary"
                  disabled={isSubmitting || !isValid}
                  type={'submit'}
                  variant="contained"
                  data-testid={'notInterestedSendButton'}
                >
                  {I18n.t('shared.send')}
                </Button>
                <Button
                  disabled={isSubmitting}
                  onClick={props.onClose}
                  data-testid={'notInterestedCancelButton'}
                >
                  {I18n.t('shared.cancel')}
                </Button>
              </DialogActions>
            </Form>
          </Dialog>
        )
      }}
    </Formik>
  )
}
